import React, { useRef, useState, useEffect } from "react";

import { Formik, Form } from "formik";
import { RightMinimalArrow } from "icons";
import { Error } from "icons";
import * as yup from "yup";

import referenceSourceValuesApi from "apis/reference_source_values";
import referenceSourceApi from "apis/reference_sources";
import usersApi from "apis/users";
import {
  Button,
  Pane,
  Table,
  Typography,
  Steps,
  FileUploader,
} from "clearsense-ui/components";
import { Textarea, Input, Select } from "clearsense-ui/components/formik";
import { CREATE_SOURCE_VALIDATION_SCHEMA } from "components/DataManagement/DataSources/constants/createReferenceSource";
import {
  SAMPLE_INIT,
  COLUMNS,
} from "components/DataManagement/DataSources/constants/uploadReferenceSource";

const UploadReferenceSource = ({
  setIsPaneOpen,
  fetchReferenceSourcesData,
}) => {
  const [step, setStep] = useState(1);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [files, setFiles] = useState();
  const [error, setError] = useState(false);
  const [createdValues, setCreatedValues] = useState([]);
  const [stewards, setStewards] = useState();
  const [referenceSource, setReferenceSource] = useState();
  const formRef = useRef();

  const resetPane = () => {
    setIsPaneOpen(false);
    setStep(1);
    setFiles();
    setError(false);
    setCreatedValues([]);
    setReferenceSource();
  };

  const onSave = async () => {
    try {
      const { data } = await referenceSourceApi.create(referenceSource);
      await referenceSourceValuesApi.associate(data?.reference_source?.id, {
        ids: createdValues.map(createdValue => createdValue.id),
      });
      resetPane();
      fetchReferenceSourcesData();
    } catch (error) {
      logger.error(error);
    }
  };

  const handleClose = async () => {
    try {
      await referenceSourceValuesApi.destroy_by_ids({
        ids: createdValues.map(createdValue => createdValue.id),
      });
      resetPane();
    } catch (error) {
      logger.error(error);
    }
  };

  const handleFilesLoaded = files => {
    setFiles(files);
  };

  const handleFilesUpload = async values => {
    if (!files) {
      setError(true);
      return;
    }

    try {
      const payload = {
        name: values?.referenceSourceName,
        description: values?.referenceSourceDescription,
        reference_source_type: values?.referenceType?.value,
        reference_source_scope: values?.scope?.value,
        drafter_id: values?.drafter?.value,
        approver_id: values?.approver?.value,
        source_type: "csv",
      };
      setReferenceSource(payload);
      const { data } = await referenceSourceValuesApi.upload({ files });
      setCreatedValues(data?.reference_source_values);
      setIsButtonLoading(true);
      setStep(2);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsButtonLoading(false);
    }
  };

  const fetchStewards = async () => {
    try {
      const { data } = await usersApi.fetch(1, 1000, "");
      setStewards(
        data?.users?.map(user => ({ label: user.name, value: user.key }))
      );
    } catch (error) {
      logger.log(error);
    }
  };

  useEffect(() => fetchStewards(), []);

  return (
    <>
      <Pane.Header>
        <Typography style="h3" type="bold" className="-mt-3">
          {step === 1
            ? "Upload New Reference Source"
            : "Preview Reference Source Values"}
        </Typography>
        <Typography style="large">
          {step === 1
            ? "Upload a New Reference Source"
            : "View list of all values from the uploaded Reference Source"}
        </Typography>
      </Pane.Header>
      <Pane.Body className="py-8">
        <Steps
          steps={["Create Source", "Verify Unique Values"]}
          currentStep={step}
          setCurrentStep={setStep}
        />
        {step === 1 ? (
          <Formik
            onSubmit={handleFilesUpload}
            initialValues={{
              ...SAMPLE_INIT,
            }}
            validationSchema={yup.object(CREATE_SOURCE_VALIDATION_SCHEMA)}
            innerRef={formRef}
          >
            <Form className="grid w-full gap-4">
              <Input
                name="referenceSourceName"
                label="Reference Source Name"
                placeholder="Enter Name for the Reference Source"
                required
                data-cy="reference-source-name-input-field"
              />
              <Textarea
                name="referenceSourceDescription"
                label="Description"
                placeholder="Enter reference list description"
                data-cy="reference-source-description-text-area"
              />
              <Select
                name="referenceType"
                label="Reference Type"
                placeholder="Select Reference Type"
                options={[
                  { value: "enterprise", label: "Enterprise" },
                  { value: "others", label: "Others" },
                ]}
                className="w-72"
                required
                data-cy="reference-source-type-selector"
              />

              <Select
                name="scope"
                label="Scope"
                placeholder="Select Scope of the Reference List"
                options={[
                  { value: "public", label: "Public" },
                  { value: "private", label: "Private" },
                ]}
                className="w-72"
                required
                data-cy="reference-source-scope-selector"
              />
              <Select
                name="drafter"
                label="Drafter"
                placeholder="Select Steward"
                options={stewards}
                className="w-72"
                required
                data-cy="reference-source-drafter-selector"
              />
              <Select
                name="approver"
                label="Approver"
                placeholder="Select Steward"
                options={stewards}
                className="w-72"
                required
                data-cy="reference-source-approver-selector"
              />
              <FileUploader
                label="Upload Reference Source "
                className="w-full mt-2"
                onUploadFinish={handleFilesLoaded}
                setIsButtonLoading={setIsButtonLoading}
                accept="text/csv"
                required
              />
              {error && (
                <p
                  className="cs-ui-input__error"
                  data-cy="upload-reference-source-error"
                >
                  <Error size={15} />
                  Please upload atleast one valid CSV file.
                </p>
              )}
            </Form>
          </Formik>
        ) : (
          <>
            <Typography />
            <Table fixedHeight rowData={createdValues} columnData={COLUMNS} />
          </>
        )}
      </Pane.Body>
      <Pane.Footer>
        {step === 1 ? (
          <div className="ml-auto">
            <Button label="Cancel" style="secondary" onClick={resetPane} />
            <Button
              label="Next Step"
              onClick={() => {
                formRef.current.submitForm();
              }}
              loading={isButtonLoading}
              icon={RightMinimalArrow}
              iconPosition="right"
              className="ml-2"
              data-cy="reference-source-pane-next-button"
            />
          </div>
        ) : (
          <>
            <Button
              label="Save and Close"
              onClick={onSave}
              data-cy="reference-source-pane-save-button"
            />
            <Button
              label="Cancel"
              style="secondary"
              onClick={handleClose}
              className="ml-2"
            />
          </>
        )}
      </Pane.Footer>
    </>
  );
};

export default UploadReferenceSource;
